import { useEffect, useState } from 'react';
import { BEELDFORMATEN, frameNaarSeconde, type BeeldformaatIdModel, type ProjectModel } from '@video-studio/shared';
import type { useEditor } from '../state/useEditor';

/**
 * De bovenbalk: projectnaam, opslagstatus, beeldformaat en exporteren.
 */
export interface TaakbalkProps {
  editor: ReturnType<typeof useEditor>;
  project: ProjectModel;
  duurInFrames: number;
  onTerug: () => void;
  onUitloggen?: () => void;
  onExport: () => void;
  onBeeldformaat: (breedte: number, hoogte: number, formaat: BeeldformaatIdModel) => void;
}

export function Taakbalk({
  editor,
  project,
  duurInFrames,
  onTerug,
  onUitloggen,
  onExport,
  onBeeldformaat,
}: TaakbalkProps) {
  const [naam, setNaam] = useState(project.naam);

  useEffect(() => {
    setNaam(project.naam);
  }, [project.naam]);

  const statusTekst = (() => {
    switch (editor.opslaStatus) {
      case 'opgeslagen':
        return editor.laatsteOpgeslagen
          ? `Opgeslagen om ${new Date(editor.laatsteOpgeslagen).toLocaleTimeString('nl-NL')}`
          : 'Opgeslagen';
      case 'gewijzigd':
        return 'Wijzigingen worden opgeslagen…';
      case 'bezig':
        return 'Bezig met opslaan…';
      case 'conflict':
        return 'Conflict: het project is elders gewijzigd';
      case 'fout':
        return `Opslaan mislukt${editor.opslaFout ? `: ${editor.opslaFout}` : ''}`;
    }
  })();

  const tweede = frameNaarSeconde(duurInFrames, project.fps);

  return (
    <header className="taakbalk">
      <div className="taakbalk__links">
        <button type="button" className="knop knop--stil" onClick={onTerug} title="Terug naar de projectenlijst">
          ← Projecten
        </button>
        <input
          className="taakbalk__naam"
          value={naam}
          onChange={(e) => setNaam(e.target.value)}
          onBlur={() => {
            const schoon = naam.trim() || 'Naamloos project';
            if (schoon !== project.naam) {
              editor.bewerk((p) => ({ ...p, naam: schoon }), 'Naam wijzigen');
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          }}
          aria-label="Projectnaam"
        />
      </div>

      <div className="taakbalk__midden">
        <span
          className={`opslagstatus opslagstatus--${editor.opslaStatus}`}
          title={editor.opslaFout ?? statusTekst}
        >
          {statusTekst}
        </span>
        {editor.opslaStatus === 'conflict' && (
          <button
            type="button"
            className="knop knop--waarschuwing"
            onClick={() => void editor.bewaarNu(true)}
            title="Mijn versie toch opslaan en de andere versie overschrijven"
          >
            Toch opslaan
          </button>
        )}
        <span className="taakbalk__duur">
          {tweede.toFixed(2)} s · {duurInFrames} frames · {project.fps} fps
        </span>
      </div>

      <div className="taakbalk__rechts">
        <button
          type="button"
          className="knop knop--stil"
          onClick={editor.undo}
          disabled={!editor.historie || editor.historie.verleden.length === 0}
          title="Ongedaan maken (Ctrl+Z)"
        >
          ↶ Ongedaan
        </button>
        <button
          type="button"
          className="knop knop--stil"
          onClick={editor.redo}
          disabled={!editor.historie || editor.historie.toekomst.length === 0}
          title="Opnieuw doen (Ctrl+Y)"
        >
          ↷ Opnieuw
        </button>

        <label className="taakbalk__formaat">
          <span>Beeldformaat</span>
          <select
            value={project.beeldformaat}
            onChange={(e) => {
              const formaat = BEELDFORMATEN[e.target.value as BeeldformaatIdModel];
              if (formaat) onBeeldformaat(formaat.breedte, formaat.hoogte, formaat.id);
            }}
          >
            {Object.values(BEELDFORMATEN).map((formaat) => (
              <option key={formaat.id} value={formaat.id}>
                {formaat.label} ({formaat.breedte}×{formaat.hoogte})
              </option>
            ))}
          </select>
        </label>

        <button type="button" className="knop knop--primair" onClick={onExport}>
          Exporteren
        </button>
        {onUitloggen && (
          <button type="button" className="knop knop--stil" onClick={onUitloggen}>
            Uitloggen
          </button>
        )}
      </div>
    </header>
  );
}
